import React, { useCallback, useEffect, useRef, useState } from 'react';
import { JSX } from '@ionic/core/components';
import { BreakpointModal } from '../BreakpointModal';
import { AddAvatarModalContainer } from './AddAvatarModal.style';
import { Container, CustomButton, ImageInput } from '@mindly/ui-components';
import { PrivateRoutes } from '../../routes/navigation';
import { patchUserAvatar } from '../../api/user';
import { updateAvatars } from '../../redux/user/reducer';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../redux/user/selector';
import CropperModal from './CropperModal';
import { MessageTypes, UpdateAvatarMessage } from '../../pages/types';
import useAgoraRtm from '../../context/AgoraContext/AgoraContext';
import { usersPsychologistsSelector } from '../../redux/usersPsychologists/selector';
import { FormattedMessage } from 'react-intl';

type AddAvatarModalProps = {
  onDidDismiss: () => void;
  defaultImage?: string;
} & JSX.IonModal;

const AddAvatarModal: React.FC<AddAvatarModalProps> = ({ defaultImage, ...props }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const loadImageRef = useRef<HTMLInputElement | null>(null);

  const [isOpenCropModal, setIsOpenCropModal] = useState(false);

  const [image, setImage] = useState<File | null>(null);
  const [imageLink, setImageLink] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const { isLoggedIn, loginAgoraClient, agoraClient } = useAgoraRtm();

  const user = useSelector(userSelector);
  const usersPsychologistList = useSelector(usersPsychologistsSelector);

  const onSubmitHandler = useCallback(
    async (event: React.FormEvent) => {
      event?.preventDefault();
      if (!image) return history.push(PrivateRoutes.PSYCHOLOGIST_LIST);

      setIsLoading(true);

      patchUserAvatar(user?.id, image, (avatar: string) => async () => {
        setIsLoading(false);
        dispatch(updateAvatars(avatar));
        props.onDidDismiss();

        try {
          if (!isLoggedIn) {
            await loginAgoraClient();
          }
          const sendDataMessage: UpdateAvatarMessage = {
            type: MessageTypes.UPDATE_AVATAR,
            fromId: user?.id ?? '',
            avatar: avatar,
          };
          if (usersPsychologistList) {
            for (const item of usersPsychologistList) {
              await agoraClient?.sendMessageToPeer({ text: JSON.stringify(sendDataMessage) }, item.id);
            }
          }
        } catch (e) {}
      });
    },
    [image]
  );

  const loadImageHandler = useCallback(() => {
    if (!loadImageRef.current) return;
    loadImageRef.current?.click();
  }, [loadImageRef]);

  const handleChangeImage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event?.target?.files?.[0]) return setImage(null);

    setImage(event.target.files[0]);

    const reader = new FileReader();
    reader.onload = () => {
      setImageLink(reader.result as any);
      setIsOpenCropModal(true);
    };
    reader.readAsDataURL(event.target.files[0]);
  }, []);

  const handleModalDismiss = useCallback(() => {
    setIsLoading(false);
    setImage(null);
    setImageLink(null);
    setIsOpenCropModal(false);
    props.onDidDismiss();
  }, [props.onDidDismiss]);

  useEffect(() => {
    if (image) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageLink(reader.result as any);
      };
      reader.readAsDataURL(image);
    }
  }, [image]);

  return (
    <BreakpointModal
      {...props}
      onIonModalDidDismiss={() => props.onDidDismiss}
      onDidDismiss={handleModalDismiss}
      closeButton={true}
      closeButtonStyle={'circular'}
      modalHeight={'345px'}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
    >
      <Container>
        <AddAvatarModalContainer onSubmit={onSubmitHandler}>
          <h3 className={'gap-10px'}>
            {defaultImage ? (
              <FormattedMessage id={'image.upload.change'} defaultMessage={'Зміна зображення'} />
            ) : (
              <FormattedMessage id={'image.upload.add'} defaultMessage={'Будь ласка, додайте своє фото'} />
            )}
          </h3>
          <p className={'gap-24px'}>
            <FormattedMessage
              id={'image.upload.text'}
              defaultMessage={'З фотографією Вашому психологу буде простіше знаходити Вас серед своїх клієнтів'}
            />
          </p>
          <div onClick={() => loadImageHandler()}>
            <ImageInput
              onChange={handleChangeImage}
              ref={loadImageRef}
              disabled={isLoading}
              loadedFileValue={imageLink ?? defaultImage}
              accept="image/png, image/jpeg"
            />
          </div>

          <CustomButton
            buttonType={'primary'}
            type={imageLink ? 'submit' : 'button'}
            loading={isLoading}
            className={'gap-30px top-24px'}
            onClick={() => !imageLink && loadImageHandler()}
          >
            {image ? (
              <FormattedMessage id={'general.save.btn'} defaultMessage={'Зберегти'} />
            ) : defaultImage ? (
              <FormattedMessage id={'image.change.btn'} defaultMessage={'Змінити фото'} />
            ) : (
              <FormattedMessage id={'image.add.btn'} defaultMessage={'Додати фото'} />
            )}
          </CustomButton>
          <CropperModal
            isOpen={isOpenCropModal}
            onDidDismiss={() => setIsOpenCropModal(false)}
            setImageFile={setImage}
            imageLink={imageLink}
          />
        </AddAvatarModalContainer>
      </Container>
    </BreakpointModal>
  );
};

export default AddAvatarModal;
